import { __assign, __read, __rest } from "tslib";
import { systemToComponent } from './react-urx';
import * as u from './urx';
import React from 'react';
import { gridSystem } from './gridSystem';
import useSize from './hooks/useSize';
import useWindowViewportRectRef from './hooks/useWindowViewportRect';
import { buildScroller, buildWindowScroller, contextPropIfNotDomElement, identity, viewportStyle } from './Virtuoso';
import { LogLevel } from './loggerSystem';
import { correctItemSize } from './utils/correctItemSize';
import { VirtuosoGridMockContext } from './utils/context';
var gridComponentPropsSystem = /*#__PURE__*/ u.system(function () {
    var itemContent = u.statefulStream(function (index) { return "Item " + index; });
    var components = u.statefulStream({});
    var context = u.statefulStream(null);
    var itemClassName = u.statefulStream('virtuoso-grid-item');
    var listClassName = u.statefulStream('virtuoso-grid-list');
    var computeItemKey = u.statefulStream(identity);
    var headerFooterTag = u.statefulStream('div');
    var scrollerRef = u.statefulStream(u.noop);
    var distinctProp = function (propName, defaultValue) {
        if (defaultValue === void 0) { defaultValue = null; }
        return u.statefulStreamFromEmitter(u.pipe(components, u.map(function (components) { return components[propName]; }), u.distinctUntilChanged()), defaultValue);
    };
    return {
        context: context,
        itemContent: itemContent,
        components: components,
        computeItemKey: computeItemKey,
        itemClassName: itemClassName,
        listClassName: listClassName,
        headerFooterTag: headerFooterTag,
        scrollerRef: scrollerRef,
        FooterComponent: distinctProp('Footer'),
        HeaderComponent: distinctProp('Header'),
        ListComponent: distinctProp('List', 'div'),
        ItemComponent: distinctProp('Item', 'div'),
        ScrollerComponent: distinctProp('Scroller', 'div'),
        ScrollSeekPlaceholder: distinctProp('ScrollSeekPlaceholder', 'div'),
    };
});
var combinedSystem = /*#__PURE__*/ u.system(function (_a) {
    var _b = __read(_a, 2), gridSystem = _b[0], gridComponentPropsSystem = _b[1];
    return __assign(__assign({}, gridSystem), gridComponentPropsSystem);
}, u.tup(gridSystem, gridComponentPropsSystem));
var GridItems = React.memo(function GridItems() {
    var gridState = useEmitterValue('gridState');
    var listClassName = useEmitterValue('listClassName');
    var itemClassName = useEmitterValue('itemClassName');
    var itemContent = useEmitterValue('itemContent');
    var computeItemKey = useEmitterValue('computeItemKey');
    var isSeeking = useEmitterValue('isSeeking');
    var scrollHeightCallback = usePublisher('scrollHeight');
    var ItemComponent = useEmitterValue('ItemComponent');
    var ListComponent = useEmitterValue('ListComponent');
    var ScrollSeekPlaceholder = useEmitterValue('ScrollSeekPlaceholder');
    var context = useEmitterValue('context');
    var itemDimensions = usePublisher('itemDimensions');
    var gridGap = usePublisher('gap');
    var log = useEmitterValue('log');
    var stateRestoreInProgress = useEmitterValue('stateRestoreInProgress');
    var listRef = useSize(function (el) {
        var scrollHeight = el.parentElement.parentElement.scrollHeight;
        scrollHeightCallback(scrollHeight);
        var firstItem = el.firstChild;
        if (firstItem) {
            var _a = firstItem.getBoundingClientRect(), width = _a.width, height = _a.height;
            itemDimensions({ width: width, height: height });
        }
        gridGap({
            row: resolveGapValue('row-gap', getComputedStyle(el).rowGap, log),
            column: resolveGapValue('column-gap', getComputedStyle(el).columnGap, log),
        });
    });
    if (stateRestoreInProgress) {
        return null;
    }
    //   console.log('rendering items', gridState.items)
    return React.createElement(ListComponent, __assign(__assign({ ref: listRef, className: listClassName }, contextPropIfNotDomElement(ListComponent, context)), { style: { paddingTop: gridState.offsetTop, paddingBottom: gridState.offsetBottom }, 'data-test-id': 'virtuoso-item-list' }), gridState.items.map(function (item) {
        var key = computeItemKey(item.index, item.data, context);
        return isSeeking
            ? React.createElement(ScrollSeekPlaceholder, __assign(__assign({ key: key }, contextPropIfNotDomElement(ScrollSeekPlaceholder, context)), { index: item.index, height: gridState.itemHeight, width: gridState.itemWidth }))
            : React.createElement(ItemComponent, __assign(__assign({}, contextPropIfNotDomElement(ItemComponent, context)), { className: itemClassName, 'data-index': item.index, key: key }), itemContent(item.index, item.data, context));
    }));
});
var Header = React.memo(function VirtuosoHeader() {
    var Header = useEmitterValue('HeaderComponent');
    var headerHeight = usePublisher('headerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return headerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Header
        ? React.createElement(headerFooterTag, { ref: ref }, React.createElement(Header, contextPropIfNotDomElement(Header, context)))
        : null;
});
var Footer = React.memo(function VirtuosoGridFooter() {
    var Footer = useEmitterValue('FooterComponent');
    var footerHeight = usePublisher('footerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return footerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Footer
        ? React.createElement(headerFooterTag, { ref: ref }, React.createElement(Footer, contextPropIfNotDomElement(Footer, context)))
        : null;
});
var Viewport = function (_a) {
    var children = _a.children;
    var ctx = React.useContext(VirtuosoGridMockContext);
    var itemDimensions = usePublisher('itemDimensions');
    var viewportDimensions = usePublisher('viewportDimensions');
    var viewportRef = useSize(function (el) {
        viewportDimensions(el.getBoundingClientRect());
    });
    React.useEffect(function () {
        if (ctx) {
            viewportDimensions({ height: ctx.viewportHeight, width: ctx.viewportWidth });
            itemDimensions({ height: ctx.itemHeight, width: ctx.itemWidth });
        }
    }, [ctx, viewportDimensions, itemDimensions]);
    return (React.createElement("div", { style: viewportStyle, ref: viewportRef }, children));
};
var WindowViewport = function (_a) {
    var children = _a.children;
    var ctx = React.useContext(VirtuosoGridMockContext);
    var windowViewportRect = usePublisher('windowViewportRect');
    var itemDimensions = usePublisher('itemDimensions');
    var customScrollParent = useEmitterValue('customScrollParent');
    var viewportRef = useWindowViewportRectRef(windowViewportRect, customScrollParent);
    React.useEffect(function () {
        if (ctx) {
            itemDimensions({ height: ctx.itemHeight, width: ctx.itemWidth });
            windowViewportRect({ offsetTop: 0, visibleHeight: ctx.viewportHeight, visibleWidth: ctx.viewportWidth });
        }
    }, [ctx, windowViewportRect, itemDimensions]);
    return (React.createElement("div", { ref: viewportRef, style: viewportStyle }, children));
};
var GridRoot = React.memo(function GridRoot(_a) {
    var props = __rest(_a, []);
    var useWindowScroll = useEmitterValue('useWindowScroll');
    var customScrollParent = useEmitterValue('customScrollParent');
    var TheScroller = customScrollParent || useWindowScroll ? WindowScroller : Scroller;
    var TheViewport = customScrollParent || useWindowScroll ? WindowViewport : Viewport;
    return (React.createElement(TheScroller, __assign({}, props),
        React.createElement(TheViewport, null,
            React.createElement(Header, null),
            React.createElement(GridItems, null),
            React.createElement(Footer, null))));
});
var _a = systemToComponent(combinedSystem, {
    optional: {
        context: 'context',
        totalCount: 'totalCount',
        overscan: 'overscan',
        itemContent: 'itemContent',
        components: 'components',
        computeItemKey: 'computeItemKey',
        data: 'data',
        initialItemCount: 'initialItemCount',
        scrollSeekConfiguration: 'scrollSeekConfiguration',
        headerFooterTag: 'headerFooterTag',
        listClassName: 'listClassName',
        itemClassName: 'itemClassName',
        useWindowScroll: 'useWindowScroll',
        customScrollParent: 'customScrollParent',
        scrollerRef: 'scrollerRef',
        logLevel: 'logLevel',
        restoreStateFrom: 'restoreStateFrom',
        initialTopMostItemIndex: 'initialTopMostItemIndex',
    },
    methods: {
        scrollTo: 'scrollTo',
        scrollBy: 'scrollBy',
        scrollToIndex: 'scrollToIndex',
    },
    events: {
        isScrolling: 'isScrolling',
        endReached: 'endReached',
        startReached: 'startReached',
        rangeChanged: 'rangeChanged',
        atBottomStateChange: 'atBottomStateChange',
        atTopStateChange: 'atTopStateChange',
        stateChanged: 'stateChanged',
    },
}, GridRoot), Grid = _a.Component, usePublisher = _a.usePublisher, useEmitterValue = _a.useEmitterValue, useEmitter = _a.useEmitter;
// Fix
export { Grid };
var Scroller = /*#__PURE__*/ buildScroller({ usePublisher: usePublisher, useEmitterValue: useEmitterValue, useEmitter: useEmitter });
var WindowScroller = /*#__PURE__*/ buildWindowScroller({ usePublisher: usePublisher, useEmitterValue: useEmitterValue, useEmitter: useEmitter });
function resolveGapValue(property, value, log) {
    if (value !== 'normal' && !(value === null || value === void 0 ? void 0 : value.endsWith('px'))) {
        log(property + " was not resolved to pixel value correctly", value, LogLevel.WARN);
    }
    if (value === 'normal') {
        return 0;
    }
    return parseInt(value !== null && value !== void 0 ? value : '0', 10);
}
export var VirtuosoGrid = Grid;
